import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Modal,
  Box,
  SpaceBetween,
  FormField,
  Input,
  Select,
  Tabs,
  DatePicker,
  Button,
  Textarea,
  Grid,
  Multiselect,
  Popover,
  StatusIndicator,
  Checkbox,
  Alert
} from '@amzn/awsui-components-react';
import { insertCruzerJobSchedule, deleteCruzerJobSchedule, editCruzerJobSchedule } from './Utils/CruzerAPIs';
import { buildEventBridgeCron, getHumanReadableExplanation, getNextRunPreviews, calculateDailyScheduleCount } from './Utils/CRONEngine';
import { fetchApps } from '../Templates/Utils/AppActivityUtils';
import { DEFAULT_LAMBDA_ARN_CONFIG, TARGET_TYPE_OPTIONS } from './Constants/SchedulerConstants';
import { createAuditLog, getCurrentTime } from '../Templates/Utils/AppActivityUtilsAppsyncPlus';
import HourlyTab from './CRONComponents/HourlyTab';
import DailyTab from './CRONComponents/DailyTab';
import WeeklyTab from './CRONComponents/WeeklyTab';
import MonthlyTab from './CRONComponents/MonthlyTab';
import CronTab from './CRONComponents/CronTab';
import { validateAwsCronExpression } from './Utils/GeneralUtils';
import './SchedulerSetupModal.scss';

const SchedulerSetupModal = ({
  visible,
  onDismiss,
  initialData = null,
  mode = 'add',
  allDatasets = [],
  setDatasets,
  setShowEditModal,
  setShowAddModal,
  setSelectedDataset,
  setShowAddSuccess,
  setAddedScheduleName,
  loadData,
  dataFromMeta = [],
  datasetOptions = []
}) => {
  // Form state
  const [schedulerName, setSchedulerName] = useState('');
  const [description, setDescription] = useState('');
  const [datasetUrl, setDatasetUrl] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState('hourly');
  const [beginScheduleOn, setBeginScheduleOn] = useState('');
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [targetType, setTargetType] = useState({ label: 'Lambda', value: 'LAMBDA' });
  const [targetIdentifier, setTargetIdentifier] = useState('');
  const [targetPayload, setTargetPayload] = useState('{"dataset_id": ""}');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showEditSuccess, setShowEditSuccess] = useState(false);
  const [flexibleSchedule, setFlexibleSchedule] = useState(false);
  // Remove local meta data state since it's now passed as props

  // Frequency configuration state
  const [frequencyConfigs, setFrequencyConfigs] = useState({
    hourly: { option: 'interval', interval: '1', selectedHours: [], rangeStart: '9', rangeEnd: '17', specificMinute: '0' },
    daily: { calendarDay: true, calendarDayBegin: '1', calendarDayEnd: '31', workingDay: false, everyXDays: false, everyXDaysValue: '2', hourlyConfig: null },
    weekly: { days: { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }, everyXWeeks: '2', hourlyConfig: null },
    monthly: { calendarDay: true, calendarDayValue: '1', calendarDayMonths: '1', workingDay: false, workingDayValue: '2', workingDayMonths: '3', hourlyConfig: null },
    cron: {}
  });

  const handleFrequencyConfigChange = (frequency, config) => {
    setFrequencyConfigs(prev => ({
      ...prev,
      [frequency]: config
    }));
  };

  // Validation errors
  const [errors, setErrors] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const userId = useSelector((globalState) => globalState.auth.userId);
  const currentAppId = useSelector((globalState) => globalState.auth.currentAppId);
  const currentStage = useSelector((globalState) => globalState.auth.currentStage);
  const defaultLambdaArn = (DEFAULT_LAMBDA_ARN_CONFIG.find(c => c.environment === currentStage) || DEFAULT_LAMBDA_ARN_CONFIG[0]).arn;

  // Apps state
  const [availableApps, setAvailableApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appsLoading, setAppsLoading] = useState(false);

  const schedulePreview = React.useMemo(() => {
    try {
      const state = {
        selectedFrequency,
        ...(frequencyConfigs[selectedFrequency] || {})
      };
      
      const cronExpression = buildEventBridgeCron(state);
      const explanation = getHumanReadableExplanation(state);
      const previews = getNextRunPreviews(state, 5);
      
      return {
        cron: cronExpression,
        explanation,
        previews
      };
    } catch (error) {
      return {
        cron: 'Invalid configuration',
        explanation: 'Please complete the schedule configuration',
        previews: []
      };
    }
  }, [selectedFrequency, frequencyConfigs]);




  // Initialize form with initial data if in edit mode
  useEffect(() => {
    if (!visible) return;
    resetForm();
    if (initialData && (mode === 'edit' || mode === 'view')) {
      const apiData = initialData.originalData || {};
      
      // Basic fields
      setSchedulerName(apiData.schedule_name || initialData.schedulerName || '');
      setDescription(apiData.description || '');
      

      // Dataset URL - create proper option format
      if (apiData.reference_id) {
        // First try to find a matching option from the current app's datasetOptions
        const matchingOption = datasetOptions.find(opt => {
          const urlParts = opt.value.split('/');
          return urlParts[urlParts.length - 2] === apiData.reference_id;
        });

        if (matchingOption) {
          setDatasetUrl(matchingOption);
        } else {
          const fallbackLabel = initialData.datasetUrl && initialData.datasetUrl !== apiData.reference_id
            ? initialData.datasetUrl
            : apiData.reference_id;
          setDatasetUrl({ label: fallbackLabel, value: apiData.reference_id });
        }
      }
      
      const scheduleType = apiData.schedule_type?.toLowerCase() === 'workday_daily' ? 'daily' : (apiData.schedule_type?.toLowerCase() || 'hourly');
      setSelectedFrequency(scheduleType);
      
      // Parse and set frequency configuration
      if (apiData.schedule_config) {
        try {
          let config = apiData.schedule_config;
          // Handle double-encoded JSON
          if (typeof config === 'string' && config.startsWith('"')) {
            config = JSON.parse(config);
          }
          if (typeof config === 'string') {
            config = JSON.parse(config);
          }
          
          
          // Update frequency configs with the parsed data
          setFrequencyConfigs(prev => ({
            ...prev,
            [scheduleType]: config
          }));
        } catch (error) {
          console.error('Failed to parse schedule_config:', error);
        }
      }
      
      // Target fields
      const targetTypeOption = TARGET_TYPE_OPTIONS.find(opt => opt.value === apiData.target_type) || 
                              { label: 'Lambda', value: 'LAMBDA' };
      setTargetType(targetTypeOption);

      setTargetIdentifier(apiData.target_identifier || defaultLambdaArn);
      
      // Parse target payload if it's a JSON string
      if (apiData.target_payload) {
        try {
          // Handle double-encoded JSON
          let payload = apiData.target_payload;
          if (typeof payload === 'string' && payload.startsWith('"')) {
            payload = JSON.parse(payload);
          }
          setTargetPayload(typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2));
        } catch (e) {
          setTargetPayload(apiData.target_payload);
        }
      }
      
      // Convert app_ids to selected applications format
      if (apiData.app_ids && Array.isArray(apiData.app_ids)) {
        const appOptions = apiData.app_ids.map(appId => ({
          label: appId, // Will be updated when apps are loaded
          value: appId
        }));
        setSelectedApplications(appOptions);
      }
      
      // Date fields - convert from API format if needed
      if (apiData.begin_schedule_on) {
        // Convert from API date format to DatePicker format (YYYY-MM-DD)
        const date = new Date(apiData.begin_schedule_on);
        const formattedDate = date.toISOString().split('T')[0];
        setBeginScheduleOn(formattedDate);
      }
      
    } else if (mode === 'add') {
      // Reset form for add mode
      resetForm();
    }
  }, [initialData, mode, visible]);

  // Load apps on component mount and when currentAppId changes
  useEffect(() => {
    if (currentAppId) {
      loadApps();
    }
  }, [currentAppId]);

  // Update selected applications when apps are loaded and we have initial data
  useEffect(() => {
    if (availableApps.length > 0 && initialData?.originalData?.app_ids && (mode === 'edit' || mode === 'view')) {
      const selectedApps = initialData.originalData.app_ids.map(appId => {
        const appOption = availableApps.find(app => app.value === appId);
        return appOption || { label: `App ${appId}`, value: appId };
      });
      setSelectedApplications(selectedApps);
    }
  }, [availableApps, initialData, mode]);

  useEffect(() => {
    if (mode === 'add') setTargetIdentifier(defaultLambdaArn);
  }, [defaultLambdaArn]);

  // Update target payload when dataset URL changes
  useEffect(() => {
    if (datasetUrl?.value) {
      const urlParts = datasetUrl.value.split('/');
      const datasetId = urlParts[urlParts.length - 2]; // ID is second to last part
      setTargetPayload(JSON.stringify({"dataset_id": datasetId}));
    }
  }, [datasetUrl]);



  const resetForm = () => {
    setSchedulerName('');
    setDescription('');
    setDatasetUrl(null);
    setSelectedApp(null);
    setSelectedFrequency('hourly');
    setBeginScheduleOn('');
    setTargetType({ label: 'Lambda', value: 'LAMBDA' });
    setTargetIdentifier(defaultLambdaArn);
    setTargetPayload('{"dataset_id": ""}');
    setErrors({});
    setFrequencyConfigs({
      hourly: { option: 'interval', interval: '1', selectedHours: [], rangeStart: '9', rangeEnd: '17', specificMinute: '0' },
      daily: { calendarDay: true, calendarDayBegin: '1', calendarDayEnd: '31', workingDay: false, everyXDays: false, everyXDaysValue: '2', hourlyConfig: null },
      weekly: { days: { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }, everyXWeeks: '2', hourlyConfig: null },
      monthly: { calendarDay: true, calendarDayValue: '1', calendarDayMonths: '1', workingDay: false, workingDayValue: '2', workingDayMonths: '3', hourlyConfig: null },
      cron: {}
    });
  };

  const loadApps = async () => {
    setAppsLoading(true);
    try {
      const apps = await fetchApps();
      const appOptions = apps.map(app => ({
        label: app.app_name,
        value: app.app_id
      }));
      setAvailableApps(appOptions);
      
      // Set current app as default selected for add mode only
      if (mode === 'add') {
        const currentApp = appOptions.find(app => app.value === currentAppId);
        if (currentApp) {
          setSelectedApplications([currentApp]);
        }
      }
    } catch (error) {
      console.error('Failed to load apps:', error);
    } finally {
      setAppsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!schedulerName.trim()) {
      newErrors.schedulerName = 'Scheduler name is required';
    }

    if (!datasetUrl) {
      newErrors.datasetUrl = 'Dataset URL is required';
    }

    if (!beginScheduleOn) {
      newErrors.beginScheduleOn = 'Begin Schedule On date is required';
    }

    if (selectedFrequency === 'cron') {
      const cronError = validateAwsCronExpression(frequencyConfigs.cron?.expression);
      if (cronError) newErrors.cronExpression = cronError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveSchedule = (formData) => {
    try {
      const cronExpression = buildEventBridgeCron({
        selectedFrequency: formData.frequency,
        ...formData.frequencyConfig
      });
      
      const explanation = getHumanReadableExplanation({
        selectedFrequency: formData.frequency,
        ...formData.frequencyConfig
      });
      
      const previews = getNextRunPreviews({
        selectedFrequency: formData.frequency,
        ...formData.frequencyConfig
      });
    } catch (error) {
      console.error('Error generating cron:', error);
    }
    
    
    const payload = {
      job_metadata: {
        target_type: formData.targetType || 'LAMBDA',
        target_identifier: formData.targetIdentifier || defaultLambdaArn,
        target_payload: formData.targetPayload || JSON.stringify({"dataset_id": formData.datasetUrl}),
      },
      job_schedule: {
        schedule_name: `${formData.schedulerName || 'schedule'}`,
        schedule_type: formData.frequency === 'daily' && formData.frequencyConfig?.workingDay ? 'WORKDAY_DAILY' : (formData.frequency ? formData.frequency.toUpperCase() : 'DAILY'),
        schedule_config: JSON.stringify(formData.frequencyConfig),
        cron_expression: buildEventBridgeCron({
          selectedFrequency: formData.frequency,
          ...formData.frequencyConfig
        }),
        description: formData.description || `${formData.frequency} schedule for ${formData.schedulerName}`,
        begin_schedule_on: formData.beginScheduleOn,
        daily_schedule_count: calculateDailyScheduleCount(formData.frequency, formData.frequencyConfig),
        

        // If datasetUrl is a full URL, extract the dataset ID from the path (second-to-last segment).
        // If it's already a bare ID (fallback when no matching option found in datasetOptions), use it directly.
        // Without this check, bare IDs would return undefined from split('/').slice(-2,-1)[0], sending reference_id as null.
        reference_id: formData.datasetUrl ? (formData.datasetUrl.includes('/') ? formData.datasetUrl.split('/').slice(-2, -1)[0] : formData.datasetUrl) : '',
        app_ids: selectedApplications.map(app => app.value),
        is_active: true,
        created_by: userId,
      }
    };
    
    
    const apiCall = mode === 'edit' ? editCruzerJobSchedule : insertCruzerJobSchedule;
    const schedulePayload = mode === 'edit'
      ? { ...payload, job_schedule: { ...payload.job_schedule, schedule_id: parseInt(initialData?.schedulerId), last_updated_by: userId } }
      : payload;


    setSaveLoading(true);
    apiCall(
      schedulePayload,
      (response) => {
        
        const responseKey = mode === 'edit' ? 'editCruzerJobSchedule' : 'insertCruzerJobSchedule';
        if (response?.data?.[responseKey]) {
          try {
            const result = JSON.parse(response.data[responseKey]);
            if (result.error) {
              setSaveLoading(false);
              setErrorMessage(result.details || result.error);
              setShowErrorModal(true);
              return;
            }
          } catch (parseError) {
          }
        }
        
        setSaveLoading(false);
        if (mode === 'edit') {
          setShowEditModal(false);
          setShowEditSuccess(true);
          createAuditLog('CRUZER', `EDIT_SCHEDULE#${getCurrentTime()}`, 'Editing a schedule', JSON.stringify(initialData?.originalData || {}), JSON.stringify(schedulePayload), 'EDIT SCHEDULE', userId);
        } else {
          setShowAddModal(false);
          setAddedScheduleName(formData.schedulerName);
          setShowAddSuccess(true);
          createAuditLog('CRUZER', `CREATE_SCHEDULE#${getCurrentTime()}`, 'Creating a new schedule', JSON.stringify({}), JSON.stringify(schedulePayload), 'CREATE SCHEDULE', userId);
        }
        setSelectedDataset(null);
        loadData();
      },
      (error) => {
        console.error('Failed to save schedule:', error);
        setSaveLoading(false);
        setErrorMessage(error.message || 'Failed to save schedule. Please try again.');
        setShowErrorModal(true);
      }
    );
  };

  const handleDelete = () => {
    if (!initialData?.schedulerId) {
      console.error('No schedule ID found for deletion');
      return;
    }

    const deleteInput = {
      schedule_id: initialData.schedulerId
    };

    deleteCruzerJobSchedule(
      deleteInput,
      (response) => {
        
        // Remove from datasets list
        const updatedDatasets = allDatasets.filter(ds => ds.schedulerId !== initialData.schedulerId);
        setDatasets(updatedDatasets);
        setShowEditModal(false);
        setSelectedDataset(null);
      },
      (error) => {
        console.error('Failed to delete schedule:', error);
      }
    );
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const formData = {
      schedulerName,
      description,
      datasetUrl: datasetUrl?.value,
      frequency: selectedFrequency,
      beginScheduleOn,
      applications: selectedApplications,
      targetType: targetType?.value,
      targetIdentifier,
      targetPayload,
      frequencyConfig: getFrequencyConfig()
    };

    handleSaveSchedule(formData);
  };

  const getFrequencyConfig = () => {
    return frequencyConfigs[selectedFrequency] || {};
  };

  const isViewMode = mode === 'view';
  const configReady = initialData ? Object.keys(frequencyConfigs[selectedFrequency] || {}).length > 0 : true;
  const tabKey = `${initialData?.originalData?.schedule_id || 'new'}-${configReady}-${visible}`;

  const frequencyTabs = [
    {
      label: 'Hourly', id: 'hourly',
      content: <HourlyTab key={`hourly-${tabKey}`} isViewMode={isViewMode} onConfigChange={(config) => handleFrequencyConfigChange('hourly', config)} initialConfig={frequencyConfigs.hourly} />
    },
    {
      label: 'Daily', id: 'daily',
      content: <DailyTab key={`daily-${tabKey}`} isViewMode={isViewMode} onConfigChange={(config) => handleFrequencyConfigChange('daily', config)} initialConfig={frequencyConfigs.daily} />
    },
    {
      label: 'Weekly', id: 'weekly',
      content: <WeeklyTab key={`weekly-${tabKey}`} isViewMode={isViewMode} onConfigChange={(config) => handleFrequencyConfigChange('weekly', config)} initialConfig={frequencyConfigs.weekly} />
    },
    {
      label: 'Monthly', id: 'monthly',
      content: <MonthlyTab key={`monthly-${tabKey}`} isViewMode={isViewMode} onConfigChange={(config) => handleFrequencyConfigChange('monthly', config)} initialConfig={frequencyConfigs.monthly} />
    },
    {
      label: 'Cron', id: 'cron',
      content: <CronTab key={`cron-${tabKey}`} errors={errors} isViewMode={isViewMode} onConfigChange={(config) => handleFrequencyConfigChange('cron', config)} initialConfig={frequencyConfigs.cron} />
    }
  ];

  const ViewField = ({ label, value, children }) => (
    <Box>
      <Box variant="awsui-key-label" color="text-label" fontSize="body-s">{label}</Box>
      <Box fontSize="body-m" padding={{ top: 'xxs' }}>{children || value || '—'}</Box>
    </Box>
  );

  return (
    <>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        header={
          mode === 'view'
            ? `${initialData?.schedulerName || 'Scheduler'} (View only)`
            : (
              <Box className="modal-header-container">
                <Box fontSize="heading-xl" fontWeight="bold">Scheduler Setup</Box>
                <Box className="modal-header-divider" />
              </Box>
            )
        }
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onDismiss}>Cancel</Button>
              {!isViewMode && (
                <Button variant="primary" onClick={handleSave} loading={saveLoading} disabled={saveLoading}>
                  {mode === 'edit' ? 'Save' : 'Schedule'}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
      >
      {isViewMode ? (
        <SpaceBetween size="l">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <ViewField label="Scheduler Name" value={schedulerName} />
            <ViewField label="Schedule ID" value={initialData?.schedulerId || '—'} />
            <ViewField label="Begin Schedule On" value={beginScheduleOn} />
            <ViewField label="Dataset" value={datasetUrl?.label} />
            <ViewField label="Schedule">{<span>🧠 {schedulePreview.explanation}</span>}</ViewField>
            {description && <ViewField label="Description" value={description} />}
            {selectedApplications.length > 0 && <ViewField label="Associated Applications" value={selectedApplications.map(a => a.label).join(', ')} />}
          </div>
          <div style={{ borderTop: '1px solid #e9ebed', paddingTop: '16px' }}>
            <Box variant="awsui-key-label" fontSize="body-s" color="text-label">Frequency — {selectedFrequency.charAt(0).toUpperCase() + selectedFrequency.slice(1)}</Box>
            <Box padding={{ top: 's' }}>
              {frequencyTabs.find(t => t.id === selectedFrequency)?.content}
            </Box>
          </div>
        </SpaceBetween>
      ) : (
      <SpaceBetween size="l">
        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Scheduler name<Box variant="span" color="text-status-error">*</Box></strong>
          </Box>
          <FormField errorText={errors.schedulerName}>
            <Input
              value={schedulerName}
              onChange={({ detail }) => setSchedulerName(detail.value)}
              placeholder="Inset scheduler name"
            />
          </FormField>
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Description</strong>
          </Box>
          <FormField>
            <Textarea
              value={description}
              onChange={({ detail }) => setDescription(detail.value)}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </FormField>
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Dataset URL<Box variant="span" color="text-status-error">*</Box></strong>
          </Box>
          <FormField
            errorText={errors.datasetUrl}
            constraintText={mode !== 'edit' && datasetOptions.length === 0 ? 'This app has no datasets associated via Meta.' : undefined}
          >
            <Select
              selectedOption={datasetUrl}
              onChange={({ detail }) => setDatasetUrl(detail.selectedOption)}
              options={datasetOptions}
              placeholder="Select dataset from Meta Information Dependencies"
              disabled={mode === 'edit' || datasetOptions.length === 0}
            />
          </FormField>
        </Grid>

        <div className="hidden-fields">
          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
            <Box padding={{ top: 'xs' }}>
              <strong>Target Type<Box variant="span" color="text-status-error">*</Box></strong>
            </Box>
            <FormField>
              <Select selectedOption={targetType} onChange={({ detail }) => setTargetType(detail.selectedOption)} options={TARGET_TYPE_OPTIONS} placeholder="Select target type" disabled={mode === 'edit'} />
            </FormField>
          </Grid>
          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
            <Box padding={{ top: 'xs' }}>
              <strong>Target Identifier<Box variant="span" color="text-status-error">*</Box></strong>
            </Box>
            <FormField errorText={errors.targetIdentifier}>
              <Input value={targetIdentifier} onChange={({ detail }) => setTargetIdentifier(detail.value)} placeholder="Enter ARN or identifier for the target" disabled={mode === 'edit'} />
            </FormField>
          </Grid>
          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
            <Box padding={{ top: 'xs' }}><strong>Target Payload</strong></Box>
            <FormField>
              <Textarea value={targetPayload} onChange={({ detail }) => setTargetPayload(detail.value)} placeholder='Enter JSON payload' rows={4} disabled={mode === 'edit'} />
            </FormField>
          </Grid>
        </div>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Frequency<Box variant="span" color="text-status-error">*</Box></strong>
          </Box>
          <SpaceBetween size="s">
            <Alert statusIconAriaLabel="Info" type="info">
              All schedules are in UTC.
            </Alert>
            <Tabs
              activeTabId={selectedFrequency}
              onChange={({ detail }) => setSelectedFrequency(detail.activeTabId)}
              tabs={frequencyTabs}
              variant="default"
              className="scheduler-tabs"
            />
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <Checkbox checked={flexibleSchedule} onChange={({ detail }) => setFlexibleSchedule(detail.checked)} disabled={true}>
                Flexible schedule
              </Checkbox>
              <Popover
                size="medium" position="top" triggerType="custom" dismissButton={false}
                content="Enabling this option adds a random ±15-minute offset to your refresh time, helping distribute load across schedules that share the same cron expression."
              >
                <StatusIndicator type="info" />
              </Popover>
            </SpaceBetween>
          </SpaceBetween>
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}>
            <strong>Begin Schedule On<Box variant="span" color="text-status-error">*</Box></strong>
          </Box>
          <FormField errorText={errors.beginScheduleOn}>
            <DatePicker value={beginScheduleOn} onChange={({ detail }) => setBeginScheduleOn(detail.value)} placeholder="Date" />
          </FormField>
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 8 }, { colspan: 1 }]}>
          <Box padding={{ top: 'xs' }}><strong>Preview Configuration</strong></Box>
          <Box padding={{ top: 'xs' }}><strong>🧠 {schedulePreview.explanation}</strong></Box>
          <Popover
            size="large" position="top" triggerType="custom" dismissButton={false}
            content={
              <SpaceBetween size="s">
                <Box><strong>🔧 Cron:</strong> <code>{schedulePreview.cron}</code></Box>
                {/* <Box>
                  <strong>🔮 Next 5 runs:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                    {schedulePreview.previews.map((preview, index) => (
                      <li key={index} style={{ fontSize: '12px' }}>{preview}</li>
                    ))}
                  </ul>
                </Box> */}
              </SpaceBetween>
            }
          >
            <StatusIndicator type="info"></StatusIndicator>
          </Popover>
        </Grid>

        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <Box padding={{ top: 'xs' }}><strong>Add to Another Accounting Central Application</strong></Box>
          <Multiselect
            selectedOptions={selectedApplications}
            onChange={({ detail }) => {
              const currentApp = detail.selectedOptions.find(app => app.value === currentAppId);
              if (!currentApp) {
                const currentAppOption = availableApps.find(app => app.value === currentAppId);
                setSelectedApplications(currentAppOption ? [currentAppOption, ...detail.selectedOptions] : detail.selectedOptions);
              } else {
                setSelectedApplications(detail.selectedOptions);
              }
            }}
            options={availableApps}
            placeholder="Other Apps that have the same dataset in Meta"
          />
        </Grid>
      </SpaceBetween>
      )}
    </Modal>

    {/* Edit Success Modal */}
    <Modal
      visible={showEditSuccess}
      onDismiss={() => { setShowEditSuccess(false); }}
      header="Schedule Updated Successfully"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={() => { setShowEditSuccess(false); }}>OK</Button>
        </Box>
      }
    >
      <Box>Schedule <strong>{schedulerName}</strong> has been successfully updated.</Box>
    </Modal>

    {/* Error Modal */}
    <Modal
      visible={showErrorModal}
      onDismiss={() => setShowErrorModal(false)}
      header="Error"
      footer={
        <Box float="right">
          <Button 
            variant="primary" 
            onClick={() => setShowErrorModal(false)}
          >
            OK
          </Button>
        </Box>
      }
    >
      <Box>
        {errorMessage || 'Something went wrong. Please try again.'}
      </Box>
    </Modal>
    </>
  );
};

export default SchedulerSetupModal;
